import { backend_url } from "@/constants";
import { Endpoints } from "@api/endpoints";
import { AXIOS_CONFIG } from "@utils/api";
import axios from "axios";

import type { IntegrationSession } from "@components/Integrations/types";

/**
 * Fetches the current Jotform integration session.
 * @returns The Jotform session.
 * @throws An error if the session does not exist.
 */
export async function getJotformSession(): Promise<IntegrationSession> {
  try {
    const response = await axios.get(
      `${backend_url}/${Endpoints.jotform.session}`,
      AXIOS_CONFIG
    );
    return response.data.content;
  } catch (error) {
    const errorMessage = "Jotform session does not exist.";
    console.error(errorMessage);
    throw new Error(errorMessage);
  }
}

/**
 * Stores a new Jotform integration session.
 * @param apiKey - The Jotform API key.
 * @param integrationAttemptId - Optional integration attempt ID for tracking.
 * @returns The Jotform session.
 */
export async function createJotformSession(
  apiKey: string,
  integrationAttemptId?: string
): Promise<IntegrationSession> {
  try {
    const response = await axios.post(
      `${backend_url}/${Endpoints.jotform.session}`,
      {
        apiKey,
        ...(integrationAttemptId && { integrationAttemptId }),
      },
      AXIOS_CONFIG
    );
    return response.data.content;
  } catch (error) {
    const errorMessage = "Unable to create Jotform session.";
    console.error(errorMessage);
    throw new Error(errorMessage);
  }
}

/**
 * Deletes the current Jotform integration session.
 */
export async function deleteJotformSession(): Promise<void> {
  try {
    await axios.delete(
      `${backend_url}/${Endpoints.jotform.session}`,
      AXIOS_CONFIG
    );
  } catch (error) {
    const errorMessage = "Unable to delete Jotform session.";
    console.error(errorMessage);
    throw new Error(errorMessage);
  }
}

/**
 * Fetches the current Cognito Forms integration session.
 * @returns The Integrations session.
 * @throws An error if the session does not exist.
 */
export async function getCognitoSession(): Promise<IntegrationSession> {
  try {
    const response = await axios.get(
      `${backend_url}/${Endpoints.cognito.session}`,
      AXIOS_CONFIG
    );
    return response.data.content;
  } catch (error) {
    const errorMessage = "Cognito Forms session does not exist.";
    console.error(errorMessage);
    throw new Error(errorMessage);
  }
}

/**
 * Deletes the current Cognito Forms integration session.
 */
export async function deleteCognitoSession(): Promise<void> {
  try {
    await axios.delete(
      `${backend_url}/${Endpoints.cognito.session}`,
      AXIOS_CONFIG
    );
  } catch (error) {
    const errorMessage = "Unable to delete Cognito Forms session.";
    console.error(errorMessage);
    throw new Error(errorMessage);
  }
}

/**
 * Fetches the current GHL integration session.
 * @returns The GHL session.
 * @throws An error if the session does not exist.
 */
export async function getGHLSession(): Promise<IntegrationSession> {
  try {
    const response = await axios.get(
      `${backend_url}/${Endpoints.ghl.session}`,
      AXIOS_CONFIG
    );
    return response.data.content;
  } catch (error) {
    const errorMessage = "GHL session does not exist.";
    console.error(errorMessage);
    throw new Error(errorMessage);
  }
}

/**
 * Stores a new GHL integration session.
 * @param code - The GHL access code.
 * @param integrationAttemptId - Optional integration attempt ID for tracking.
 * @returns The GHL session.
 */
export async function createGHLSession(
  code: string,
  integrationAttemptId?: string
): Promise<IntegrationSession> {
  try {
    const response = await axios.post(
      `${backend_url}/${Endpoints.ghl.session}`,
      {
        code,
        ...(integrationAttemptId && { integrationAttemptId }),
      },
      AXIOS_CONFIG
    );
    return response.data.content;
  } catch (error) {
    const errorMessage = "Unable to create GHL session.";
    console.error(errorMessage);
    throw new Error(errorMessage);
  }
}

/**
 * Deletes the current GHL integration session.
 */
export async function deleteGHLSession(): Promise<void> {
  try {
    await axios.delete(`${backend_url}/${Endpoints.ghl.session}`, AXIOS_CONFIG);
  } catch (error) {
    const errorMessage = "Unable to delete GHL session.";
    console.error(errorMessage);
    throw new Error(errorMessage);
  }
}

/**
 * Fetches the current SuiteDash integration session.
 * @returns The Integrations session.
 * @throws An error if the session does not exist.
 */
export async function getSuiteDashSession(): Promise<IntegrationSession> {
  try {
    const response = await axios.get(
      `${backend_url}/${Endpoints.suitedash.session}`,
      AXIOS_CONFIG
    );
    return response.data.content;
  } catch (error) {
    const errorMessage = "SuiteDash session does not exist.";
    console.error(errorMessage);
    throw new Error(errorMessage);
  }
}

/**
 * Deletes the current SuiteDash integration session.
 */
export async function deleteSuiteDashSession(): Promise<void> {
  try {
    await axios.delete(
      `${backend_url}/${Endpoints.suitedash.session}`,
      AXIOS_CONFIG
    );
  } catch (error) {
    const errorMessage = "Unable to delete SuiteDash session.";
    console.error(errorMessage);
    throw new Error(errorMessage);
  }
}
